"use client";

import React from 'react';
import Link from 'next/link';
import { ArrowLeft, Star, Eye, Clock, Lightbulb, Heart, Shield, Brain } from 'lucide-react';
import { AboutLayout } from '@/components/about/about-layout';

export default function LePenduPage() {
  return (
    <AboutLayout activeSection="tarot">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Current Section Indicator */}
        <div className="mb-4 p-3 bg-teal-500/10 border border-teal-500/20 rounded-lg">
          <div className="flex items-center gap-2 text-teal-300">
            <Star className="h-4 w-4" />
            <span className="text-sm font-medium">🃏 Tarot</span>
            <span className="text-teal-500">/</span>
            <span className="text-sm">Marseille</span>
            <span className="text-teal-500">/</span>
            <span className="text-sm">Major Arkana</span>
            <span className="text-teal-500">/</span>
            <span className="text-sm">Le Pendu</span>
          </div>
        </div>

        {/* Breadcrumb */}
        <div className="mb-6">
          <Link
            href="/about/tarot/systems/marseille/major-arcana"
            className="inline-flex items-center text-slate-400 hover:text-slate-200 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Major Arkana'ya Geri Dön
          </Link>
        </div>

        {/* Header with Card */}
        <div className="bg-gradient-to-r from-teal-900/20 to-cyan-900/20 rounded-xl p-8 mb-8 border border-teal-500/20">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
            <div className="lg:col-span-1 flex justify-center">
              <div className="w-48 h-72 rounded-xl overflow-hidden shadow-2xl">
                <img
                  src="/images/tarot/cards/marseille-le-pendu.svg"
                  alt="Le Pendu Tarot Card"
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    const target = e.currentTarget;
                    target.style.display = 'none';
                    if (target.nextElementSibling) {
                      (target.nextElementSibling as HTMLElement).style.display = 'flex';
                    }
                  }}
                />
                <div className="hidden w-full h-full bg-gradient-to-b from-teal-900/40 to-cyan-900/40 border border-teal-500/30 flex-col items-center justify-center text-teal-300">
                  <span className="text-3xl font-bold mb-2">XII</span>
                  <span className="text-lg font-semibold">LE PENDU</span>
                  <span className="text-sm text-center mt-2">The Hanged Man</span>
                </div>
              </div>
            </div>
            <div className="lg:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-cyan-700 rounded-full flex items-center justify-center">
                  <span className="text-white font-bold text-lg">XII</span>
                </div>
                <div>
                  <h1 className="text-4xl font-bold text-slate-100">Le Pendu</h1>
                  <p className="text-xl text-teal-400">Asılan Adam / Fedakarlık ve Perspektif Değişimi</p>
                </div>
              </div>
              <p className="text-lg text-slate-200 leading-relaxed mb-4">
                Marseille geleneğinde on ikinci numara. Fedakarlık, bekleme ve yeni perspektif kazanmayı
                temsil eden Le Pendu, manevi aydınlanma ve içsel dönüşümü simgeler. Bu kart,
                zorunlu duraklama ve sabır ile elde edilecek bilgeliği anlatır.
              </p>
              <div className="flex flex-wrap gap-2">
                <span className="px-3 py-1 bg-teal-500/20 text-teal-300 rounded-full text-sm">Fedakarlık</span>
                <span className="px-3 py-1 bg-teal-500/20 text-teal-300 rounded-full text-sm">Perspektif</span>
                <span className="px-3 py-1 bg-teal-500/20 text-teal-300 rounded-full text-sm">Bekleme</span>
                <span className="px-3 py-1 bg-teal-500/20 text-teal-300 rounded-full text-sm">Aydınlanma</span>
              </div>
            </div>
          </div>
        </div>

        {/* Card Meanings */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Positive Meanings */}
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <h2 className="text-2xl font-semibold text-slate-100 mb-4 flex items-center gap-2">
              <Eye className="h-6 w-6 text-green-400" />
              Düz Pozisyon Anlamları
            </h2>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Fedakarlık ve Kabul</h3>
                  <p className="text-slate-400 text-sm">Gönüllü vazgeçme, daha büyük bir amaç için fedakarlık yapma.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Yeni Perspektif</h3>
                  <p className="text-slate-400 text-sm">Olaylara farklı açıdan bakma, yeni anlayış kazanma.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Sabır ve Bekleme</h3>
                  <p className="text-slate-400 text-sm">Doğru zamanın gelmesini bekleme, sabrın getirdiği hikmet.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Manevi Aydınlanma</h3>
                  <p className="text-slate-400 text-sm">İçsel dönüşüm, ruhsal gelişim ve yüce bilgi.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Negative Meanings */}
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <h2 className="text-2xl font-semibold text-slate-100 mb-4 flex items-center gap-2">
              <Clock className="h-6 w-6 text-red-400" />
              Ters Pozisyon Anlamları
            </h2>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Gereksiz Fedakarlık</h3>
                  <p className="text-slate-400 text-sm">Anlamsız vazgeçmeler, kendini heba etme ve martir kompleksi.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Kararlılık Eksikliği</h3>
                  <p className="text-slate-400 text-sm">Sürekli bekleme, eylemsizlik ve fırsat kaçırma.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Direnç ve İnatçılık</h3>
                  <p className="text-slate-400 text-sm">Değişime karşı direnç, eski yollarda inatçılık.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Oyalanış ve Gecikme</h3>
                  <p className="text-slate-400 text-sm">Zamanı boşa harcama, sürekli erteleme ve kararsızlık.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Symbolism */}
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Marseille Sembolizmi</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-teal-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">🙃</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Baş Aşağı Asılan Adam</h3>
              <p className="text-slate-400 text-sm">Tersine dönmüş bakış açısı, yeni perspektif.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-amber-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">🌳</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Ağaç</h3>
              <p className="text-slate-400 text-sm">Hayat ağacı, manevi büyüme ve köklere bağlı kalma.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-gray-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">🪢</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Ayak Bağları</h3>
              <p className="text-slate-400 text-sm">Gönüllü sınırlandırma, fedakarlık ve kabul.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-yellow-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <Lightbulb className="h-6 w-6 text-white" />
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Hale</h3>
              <p className="text-slate-400 text-sm">Manevi aydınlanma, yüce bilgi ve kutsal aydınlık.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">👗</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Kırmızı Elbise</h3>
              <p className="text-slate-400 text-sm">Tutku, hayat gücü ve aktif enerji.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">👖</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Mavi Pantolon</h3>
              <p className="text-slate-400 text-sm">Huzur, manevi denge ve içsel sakinlik.</p>
            </div>
          </div>
        </div>

        {/* Life Areas */}
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Yaşam Alanlarındaki Anlamlar</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Heart className="h-5 w-5 text-pink-400" />
                Aşk ve İlişkiler
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• İlişki için fedakarlık</li>
                <li>• Yeni bakış açısı kazanma</li>
                <li>• Sabırlı bekleme</li>
                <li>• Manevi bağ kurma</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Star className="h-5 w-5 text-yellow-400" />
                Kariyer ve İş
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Kariyerde mola</li>
                <li>• Yeni yaklaşım bulma</li>
                <li>• Stratejik bekleme</li>
                <li>• Değerlerin gözden geçirme</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Brain className="h-5 w-5 text-purple-400" />
                Kişisel Gelişim
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• İç gözlem ve meditasyon</li>
                <li>• Perspektif değişimi</li>
                <li>• Sabır geliştirme</li>
                <li>• Özverili davranma</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Shield className="h-5 w-5 text-teal-400" />
                Manevi Gelişim
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Ruhsal dönüşüm</li>
                <li>• Manevi fedakarlık</li>
                <li>• Yüce bilgi edinme</li>
                <li>• Karmik ders alma</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex justify-between items-center">
          <Link
            href="/about/tarot/systems/marseille/major-arcana/la-force"
            className="inline-flex items-center px-4 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded-lg text-slate-300 hover:text-slate-100 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Önceki: La Force
          </Link>

          <Link
            href="/about/tarot/systems/marseille/major-arcana/l-arcane-sans-nom"
            className="inline-flex items-center px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg transition-colors"
          >
            Sonraki: L'Arcane sans nom
            <ArrowLeft className="h-4 w-4 ml-2 rotate-180" />
          </Link>
        </div>
      </div>
    </AboutLayout>
  );
}